import React, { useState, useEffect, useCallback } from 'react';
import { auth } from '../firebase';
import TopNavigation from '../components/TopNavigation';
import { getEarningsMe, requestWithdrawal } from '../utils/secureFunctions';

interface EarningsData {
  totalEarnings: number;
  onHold: number;
  withdrawn: number;
  available: number;
  pendingWithdrawals: number;
  paidImpressions: number;
  paidClicks: number;
  musicRoyalties?: number;
}

interface Eligibility {
  eligible: boolean;
  followers: number;
  watchHours: number;
  accountAgeDays: number;
  accountAgeOk?: boolean;
  requirements: { minFollowers: number; minWatchHours: number; minAccountAgeDays: number };
  missing: string[];
}

interface Program {
  revenueSharePercent?: number;
  holdDays: number;
  minWithdrawalUsd: number;
  musicArtistSplitPercent?: number;
  minAdWatchSeconds?: number;
}

interface Withdrawal {
  id: string;
  amount: number;
  status: string;
  failureReason?: string | null;
  paypalEmail?: string | null;
  createdAt: string;
}

const usd = (n: number) => `$${(Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const statusLabel: Record<string, string> = {
  pending: 'In review',
  processing: 'Sending to PayPal',
  completed: 'Paid',
  failed: 'Failed (refunded)',
};

const EarningsDashboard: React.FC = () => {
  const [earnings, setEarnings] = useState<EarningsData | null>(null);
  const [eligibility, setEligibility] = useState<Eligibility | null>(null);
  const [program, setProgram] = useState<Program>({ holdDays: 14, minWithdrawalUsd: 50 });
  const [withdrawals, setWithdrawals] = useState<Withdrawal[]>([]);
  const [loading, setLoading] = useState(true);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [paypalEmail, setPaypalEmail] = useState(auth.currentUser?.email || '');
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const currentUid = auth.currentUser?.uid || null;

  const loadEarningsData = useCallback(async () => {
    if (!currentUid) return;
    try {
      const res: any = await getEarningsMe();
      setEarnings(res.earnings as EarningsData);
      if (res.eligibility) setEligibility(res.eligibility as Eligibility);
      if (res.program) setProgram(res.program as Program);
      setWithdrawals((res.withdrawals || []) as Withdrawal[]);
    } catch (error) {
      console.error('Failed to load earnings:', error);
      setMessage({ kind: 'error', text: 'Could not load your earnings. Please try again.' });
    } finally {
      setLoading(false);
    }
  }, [currentUid]);

  useEffect(() => {
    loadEarningsData();
  }, [loadEarningsData]);

  const available = earnings?.available || 0;
  const hasActiveWithdrawal = withdrawals.some((w) => w.status === 'pending' || w.status === 'processing');
  const canWithdraw = !!eligibility?.eligible && available >= program.minWithdrawalUsd && !hasActiveWithdrawal;

  const handleWithdrawRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    const amount = Math.floor(parseFloat(withdrawAmount) * 100) / 100;
    if (!Number.isFinite(amount) || amount < program.minWithdrawalUsd) {
      setMessage({ kind: 'error', text: `The minimum withdrawal is ${usd(program.minWithdrawalUsd)}.` });
      return;
    }
    if (amount > available) {
      setMessage({ kind: 'error', text: 'Amount exceeds your available balance.' });
      return;
    }
    setSubmitting(true);
    try {
      await requestWithdrawal({ amount, currency: 'USD', method: 'paypal', paypalEmail: paypalEmail.trim() });
      setMessage({
        kind: 'ok',
        text: `Withdrawal request of ${usd(amount)} received. Payment will be sent to your PayPal (${paypalEmail.trim()}) within 24–48 hours. We'll notify you once it's paid.`,
      });
      setShowWithdrawModal(false);
      setWithdrawAmount('');
      loadEarningsData();
    } catch (error: any) {
      const text = error?.details?.message || error?.message || 'Withdrawal failed. Please try again.';
      setMessage({ kind: 'error', text: String(text) });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div style={styles.loading}>Loading earnings...</div>;
  }

  const req = eligibility?.requirements;
  const progress = eligibility && req ? [
    { label: 'Followers', have: eligibility.followers, need: req.minFollowers, done: eligibility.followers >= req.minFollowers },
    { label: 'Watch hours (videos & Pulse)', have: Math.floor(eligibility.watchHours || 0), need: req.minWatchHours, done: Math.floor(eligibility.watchHours || 0) >= req.minWatchHours },
    { label: `Account age (days, must be over ${req.minAccountAgeDays})`, have: eligibility.accountAgeDays, need: req.minAccountAgeDays, done: !!eligibility.accountAgeOk },
  ] : [];

  return (
    <div style={styles.container}>
      <TopNavigation />
      <div style={styles.content}>
        <h1 style={styles.title}>Creator Earnings</h1>

        {message && (
          <div style={{ ...styles.warningBanner, background: message.kind === 'ok' ? '#E8F7EF' : '#FDECEA', borderColor: message.kind === 'ok' ? '#A5D6B7' : '#F5C2C0' }}>
            {message.text}
          </div>
        )}

        <div style={{ ...styles.warningBanner, background: '#fff', borderColor: '#E8F5F1' }}>
          <p style={{ margin: 0 }}>
            Monetized creators share in ad revenue from genuine views of their content. Earnings become withdrawable to PayPal after a
            short review period (minimum {usd(program.minWithdrawalUsd)}). See the{' '}
            <a href="/terms#monetization" style={{ color: '#0B6B6F', fontWeight: 600 }}>Creator Monetization terms</a>.
          </p>
          <button type="button" style={styles.verifyButton} onClick={() => { window.location.href = '/artist-verification'; }}>
            Verification centre
          </button>
        </div>

        {eligibility && !eligibility.eligible && (
          <div style={styles.warningBanner}>
            <p><strong>Monetization locked</strong></p>
            <p style={{ marginTop: 6 }}>Meet these requirements and monetization switches on automatically:</p>
            <ul style={{ margin: '8px 0 0 18px' }}>
              {progress.map((p) => (
                <li key={p.label}>
                  {p.label}: <strong>{p.have.toLocaleString()}</strong> / {p.need.toLocaleString()} {p.done ? '✓' : ''}
                </li>
              ))}
              {eligibility.missing.filter((m) => m.startsWith('Account is restricted')).map((m) => <li key={m}>{m}</li>)}
            </ul>
          </div>
        )}

        <div style={styles.summaryGrid}>
          <div style={styles.summaryCard}>
            <div style={styles.summaryValue}>{usd(available)}</div>
            <div style={styles.summaryLabel}>Available to withdraw</div>
          </div>
          <div style={styles.summaryCard}>
            <div style={styles.summaryValue}>{usd(earnings?.onHold || 0)}</div>
            <div style={styles.summaryLabel}>On hold ({program.holdDays} days)</div>
          </div>
          <div style={styles.summaryCard}>
            <div style={styles.summaryValue}>{usd(earnings?.totalEarnings || 0)}</div>
            <div style={styles.summaryLabel}>Total ad earnings</div>
          </div>
          <div style={styles.summaryCard}>
            <div style={styles.summaryValue}>{usd(earnings?.pendingWithdrawals || 0)}</div>
            <div style={styles.summaryLabel}>Withdrawal in progress</div>
          </div>
          <div style={styles.summaryCard}>
            <div style={styles.summaryValue}>{usd(earnings?.withdrawn || 0)}</div>
            <div style={styles.summaryLabel}>Paid out</div>
          </div>
          <div style={styles.summaryCard}>
            <div style={styles.summaryValue}>{(earnings?.paidImpressions || 0).toLocaleString()}</div>
            <div style={styles.summaryLabel}>Paid ad views · {(earnings?.paidClicks || 0).toLocaleString()} clicks</div>
          </div>
          {(earnings?.musicRoyalties || 0) > 0 && (
            <div style={styles.summaryCard}>
              <div style={styles.summaryValue}>{usd(earnings?.musicRoyalties || 0)}</div>
              <div style={styles.summaryLabel}>Music royalties (included in total)</div>
            </div>
          )}
        </div>

        <button
          style={{ ...styles.withdrawBtn, opacity: canWithdraw ? 1 : 0.5, cursor: canWithdraw ? 'pointer' : 'not-allowed' }}
          disabled={!canWithdraw}
          onClick={() => { setMessage(null); setWithdrawAmount(available.toFixed(2)); setShowWithdrawModal(true); }}
        >
          Withdraw to PayPal
        </button>
        {!canWithdraw && (
          <p style={{ color: '#5B6E6E', fontSize: 13, marginTop: 8 }}>
            {hasActiveWithdrawal
              ? 'You have a withdrawal in progress.'
              : !eligibility?.eligible
                ? 'Withdrawals open once you meet the monetization requirements.'
                : `You need at least ${usd(program.minWithdrawalUsd)} available to withdraw.`}
          </p>
        )}

        {showWithdrawModal && (
          <div style={styles.modal}>
            <div style={styles.modalContent}>
              <h2>Withdraw to PayPal</h2>
              <form onSubmit={handleWithdrawRequest}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Amount (USD)</label>
                  <input
                    type="number"
                    min={program.minWithdrawalUsd}
                    max={available}
                    step="0.01"
                    value={withdrawAmount}
                    onChange={(e) => setWithdrawAmount(e.target.value)}
                    required
                    style={styles.input}
                  />
                  <small style={{ color: '#666', marginTop: 4, display: 'block' }}>
                    Min {usd(program.minWithdrawalUsd)} · Available {usd(available)}
                  </small>
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>PayPal email</label>
                  <input
                    type="email"
                    value={paypalEmail}
                    onChange={(e) => setPaypalEmail(e.target.value)}
                    required
                    style={styles.input}
                    autoComplete="email"
                  />
                  <small style={{ color: '#666', marginTop: 4, display: 'block' }}>
                    Money is sent to this PayPal account within 24–48 hours. From PayPal you can move it to your bank.
                  </small>
                </div>
                <div style={styles.modalActions}>
                  <button type="submit" style={styles.submitBtn} disabled={submitting}>{submitting ? 'Sending…' : 'Withdraw'}</button>
                  <button type="button" onClick={() => setShowWithdrawModal(false)} style={styles.cancelBtn}>Cancel</button>
                </div>
              </form>
            </div>
          </div>
        )}

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Withdrawals</h2>
          {withdrawals.length === 0 ? (
            <p style={styles.emptyState}>No withdrawals yet</p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Date</th>
                    <th style={styles.th}>PayPal</th>
                    <th style={styles.th}>Amount</th>
                    <th style={styles.th}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {withdrawals.map((w) => (
                    <tr key={w.id}>
                      <td style={styles.td}>{w.createdAt ? new Date(w.createdAt).toLocaleDateString() : '—'}</td>
                      <td style={styles.td}>{w.paypalEmail || '—'}</td>
                      <td style={styles.td}>{usd(w.amount)}</td>
                      <td style={styles.td}>
                        <span style={{ ...styles.statusBadge, background: w.status === 'completed' ? '#4CAF50' : w.status === 'failed' ? '#f44336' : '#FF9800' }}>
                          {statusLabel[w.status] || w.status}
                        </span>
                        {w.status === 'failed' && w.failureReason && <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>{w.failureReason}</div>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
const styles: { [key: string]: React.CSSProperties } = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#F5EDE1',
  },
  content: {
    paddingTop: 20,
    maxWidth: 1200,
    margin: '0 auto',
    padding: '20px 30px',
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
  },
  title: {
    fontSize: 32,
    fontWeight: 'bold',
    color: '#072B2C',
    marginBottom: 24,
  },
  warningBanner: {
    background: '#FFF3CD',
    border: '1px solid #FFE082',
    borderRadius: 8,
    padding: 16,
    marginBottom: 24,
  },
  verifyButton: {
    marginTop: 12,
    padding: '10px 20px',
    background: '#0B6B6F',
    color: '#fff',
    border: 'none',
    borderRadius: 6,
    fontWeight: '600',
    cursor: 'pointer',
  },
  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: 20,
    marginBottom: 32,
  },
  summaryCard: {
    background: '#fff',
    borderRadius: 12,
    padding: 24,
    textAlign: 'center',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  },
  summaryValue: {
    fontSize: 28,
    fontWeight: 'bold',
    color: '#0B6B6F',
  },
  summaryLabel: {
    fontSize: 14,
    color: '#5B6E6E',
    marginTop: 8,
  },
  withdrawBtn: {
    width: '100%',
    padding: 16,
    background: '#66EAD1',
    color: '#072B2C',
    border: 'none',
    borderRadius: 8,
    fontSize: 16,
    fontWeight: '600',
    cursor: 'pointer',
    marginBottom: 32,
  },
  modal: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  modalContent: {
    background: '#fff',
    borderRadius: 12,
    padding: 32,
    maxWidth: 500,
    width: '90%',
  },
  formGroup: {
    marginBottom: 20,
  },
  label: {
    display: 'block',
    fontSize: 14,
    fontWeight: '500',
    marginBottom: 8,
    color: '#072B2C',
  },
  input: {
    width: '100%',
    padding: 12,
    borderRadius: 8,
    border: '1px solid #ccc',
    fontSize: 14,
  },
  modalActions: {
    display: 'flex',
    gap: 12,
    marginTop: 24,
  },
  submitBtn: {
    flex: 1,
    padding: 12,
    background: '#0B6B6F',
    color: '#fff',
    border: 'none',
    borderRadius: 6,
    fontWeight: '600',
    cursor: 'pointer',
  },
  cancelBtn: {
    flex: 1,
    padding: 12,
    background: '#ccc',
    color: '#333',
    border: 'none',
    borderRadius: 6,
    fontWeight: '600',
    cursor: 'pointer',
  },
  section: {
    marginTop: 40,
  },
  sectionTitle: {
    fontSize: 24,
    fontWeight: '600',
    color: '#072B2C',
    marginBottom: 20,
  },
  emptyState: {
    textAlign: 'center',
    color: '#999',
    padding: 40,
  },
  table: {
    width: '100%',
    background: '#fff',
    borderRadius: 8,
    overflow: 'hidden',
  },
  th: {
    background: '#0B6B6F',
    color: '#fff',
    padding: 12,
    textAlign: 'left',
    fontWeight: '600',
  },
  td: {
    padding: 12,
    borderBottom: '1px solid #eee',
  },
  statusBadge: {
    padding: '4px 8px',
    borderRadius: 4,
    fontSize: 12,
    color: '#fff',
    fontWeight: '500',
  },
};

export default EarningsDashboard;



